import React, { useState, useEffect, useRef } from 'react'
import { V, serif, btnGold } from '../theme'

function FeedbackCard({ chapterId, chapterTitle, type = 'chapter', onComplete }) {
  const getFeedbackList = () => { try { return JSON.parse(localStorage.getItem('tlaw_feedback') || '[]'); } catch { return []; } };
  const prevEntry = getFeedbackList().find(e => e.chapterId === chapterId && !e.skipped);
  const isFirstEver = getFeedbackList().filter(e => !e.skipped).length === 0;

  const [clarity, setClarity] = useState(prevEntry?.clarity || null);
  const [confidence, setConfidence] = useState(prevEntry?.confidence || null);
  const [openQ, setOpenQ] = useState(prevEntry?.openQuestion || '');
  const [submitted, setSubmitted] = useState(!!prevEntry);
  const [editing, setEditing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const clarityLabels    = ['','Very unclear','Somewhat unclear','Mostly clear','Clear','Very clear'];
  const confidenceLabels = ['','Not confident yet','Slightly confident','Getting there','Fairly confident','Very confident'];

  const submitFeedback = () => {
    if (!clarity || !confidence) return;
    const entry = { id: Date.now(), date: new Date().toISOString(), chapterId, chapterTitle, type, clarity, confidence, openQuestion: openQ.trim(), skipped: false };
    const all = getFeedbackList().filter(e => e.chapterId !== chapterId);
    all.push(entry);
    localStorage.setItem('tlaw_feedback', JSON.stringify(all));
    setSubmitted(true); setEditing(false);
    // Fire-and-forget email notification — never blocks the UI
    fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chapterId, chapterTitle, type, clarity, confidence, openQuestion: openQ.trim(), date: new Date().toISOString(), studentName: localStorage.getItem('tlaw_student_name') || 'Unknown' }),
    }).catch(err => console.log('Feedback email failed:', err));
  };

  const skipFeedback = () => { setDismissed(true); };

  if (dismissed) return null;

  if (submitted && !editing) {
    return (
      <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '8px', padding: '12px 18px', marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>✓ Feedback submitted for this {type === 'chapter' ? 'chapter' : 'practice'}</span>
        <button style={{ background: 'none', border: 'none', color: V.gold, fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }} onClick={() => setEditing(true)}>Update feedback →</button>
      </div>
    );
  }

  return (
    <div onClick={e => e.stopPropagation()} style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '28px', marginTop: '32px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <span style={{ fontSize: '16px', color: V.gold }}>♀</span>
        <span style={{ ...serif, fontSize: '20px', color: V.gold }}>Before you continue</span>
      </div>
      <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginBottom: '20px', lineHeight: 1.65 }}>
        Two quick questions. Your feedback shapes this program for every woman who comes after you.
      </p>
      {isFirstEver && (
        <div style={{ borderLeft: `2px solid ${V.gold}`, padding: '12px 16px', marginBottom: '16px' }}>
          <p style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.7, margin: 0 }}>
            <span style={{ color: V.gold, marginRight: '6px' }}>♀</span>
            This program gets better with your input. Two quick questions after each chapter help identify what's working and what needs more clarity. Your answers are private and shape the curriculum for every woman who comes after you.
          </p>
        </div>
      )}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', color: V.text, fontFamily: "'Jost',sans-serif", marginBottom: '8px' }}>How clear was this {type === 'chapter' ? 'chapter' : 'practice'}?</div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
          {[1,2,3,4,5].map(n => (
            <span key={n} onClick={e => { e.stopPropagation(); setClarity(n); }}
              style={{ fontSize: '26px', cursor: 'pointer', lineHeight: 1, userSelect: 'none', opacity: n <= (clarity || 0) ? 1 : 0.35, transition: 'opacity 0.15s', minWidth: '44px', textAlign: 'center' }}>
              ⭐
            </span>
          ))}
        </div>
        {clarity && <div style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic' }}>{clarityLabels[clarity]}</div>}
      </div>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', color: V.text, fontFamily: "'Jost',sans-serif", marginBottom: '8px' }}>How confident do you feel about this concept now?</div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
          {[1,2,3,4,5].map(n => (
            <span key={n} onClick={e => { e.stopPropagation(); setConfidence(n); }}
              style={{ fontSize: '26px', cursor: 'pointer', lineHeight: 1, userSelect: 'none', opacity: n <= (confidence || 0) ? 1 : 0.35, transition: 'opacity 0.15s', minWidth: '44px', textAlign: 'center' }}>
              ⭐
            </span>
          ))}
        </div>
        {confidence && <div style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic' }}>{confidenceLabels[confidence]}</div>}
      </div>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', color: V.text, fontFamily: "'Jost',sans-serif", marginBottom: '8px' }}>
          What question do you still have? <span style={{ color: V.dim, fontSize: '12px' }}>(optional)</span>
        </div>
        <textarea
          value={openQ} onChange={e => setOpenQ(e.target.value)} rows={3}
          placeholder="Anything still unclear or something you'd like to understand better..."
          style={{ width: '100%', boxSizing: 'border-box', background: '#18181c', border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '6px', padding: '10px 14px', color: '#f0ede6', fontSize: '13px', fontFamily: "'Jost',sans-serif", resize: 'vertical', outline: 'none', lineHeight: 1.6 }}
        />
      </div>
      <button onClick={e => { e.stopPropagation(); submitFeedback(); }} disabled={!clarity || !confidence}
        style={{ ...btnGold, width: '100%', height: '48px', fontSize: '14px', marginBottom: '10px', opacity: (!clarity || !confidence) ? 0.35 : 1, cursor: (!clarity || !confidence) ? 'not-allowed' : 'pointer' }}>
        Submit & Continue →
      </button>
      <div style={{ textAlign: 'center' }}>
        <button style={{ background: 'none', border: 'none', color: V.muted, fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }} onClick={e => { e.stopPropagation(); skipFeedback(); }}>Skip for now →</button>
      </div>
    </div>
  );
}


function KeyTermPill({ term, definition, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [openLeft, setOpenLeft] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleToggle = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setOpenLeft(rect.left > window.innerWidth / 2);
    }
    setOpen(o => !o);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={handleToggle} style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', color: V.gold, fontFamily: "'Jost',sans-serif", cursor: 'pointer', whiteSpace: 'nowrap' }}>
        {term}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: openLeft ? 'auto' : '0', right: openLeft ? '0' : 'auto', marginTop: '6px', background: V.bg2, border: `1px solid rgba(201,168,76,0.3)`, borderRadius: '10px', padding: '14px 16px', width: '260px', zIndex: 1000, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', fontWeight: 700, color: V.gold, marginBottom: '6px' }}>{term}</div>
          <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, lineHeight: '1.7', margin: '0 0 10px' }}>{definition}</p>
          <div style={{ borderTop: `0.5px solid ${V.border}`, paddingTop: '8px' }}>
            <button onClick={() => { setOpen(false); onNavigate && onNavigate('glossary'); }} style={{ background: 'none', border: 'none', color: V.gold, fontSize: '11px', fontFamily: "'Jost',sans-serif", cursor: 'pointer', padding: 0 }}>View in Glossary →</button>
          </div>
        </div>
      )}
    </div>
  );
}


function KeyTermsCard({ terms, onNavigate }) {
  return (
    <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '20px 24px', marginBottom: '24px' }}>
      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.muted, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '6px' }}>KEY TERMS IN THIS CHAPTER</div>
      <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.dim, fontStyle: 'italic', margin: '0 0 14px', lineHeight: '1.6' }}>Click any term to see its definition. New to these words? Read them now — they will feel familiar when you encounter them in the chapter.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {terms.map(({ term, definition }) => (
          <KeyTermPill key={term} term={term} definition={definition} onNavigate={onNavigate} />
        ))}
      </div>
    </div>
  );
}


export { FeedbackCard, KeyTermPill, KeyTermsCard }
export default FeedbackCard
